import { AlertCircle, Calendar } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "wouter";

import { ModerationFilters, ModerationRequestsTable, CancellationTable } from "./ui";
import { ExtensionRebiddingTable } from "./ui/ExtensionRebiddingTable";

import {
  useGetOnModerationOrders,
  useGetContinueRebiddingRequests,
  useGetCancelOrders,
  useAcceptOrder,
  useAcceptOrder1,
  useApproveContinueRebidding,
  type OnModerationOrderDto,
} from "@/common/api";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  ConfirmModal,
  LoadingModal,
  CommentDialog,
} from "@/common/components";
import { ROUTES } from "@/common/config";
import { useToast } from "@/common/hooks";

export function ModerationPage() {
  const { toast } = useToast();

  const [, setLocation] = useLocation();

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    requestId: string;
    action: "approve" | "reject";
  }>({
    isOpen: false,
    requestId: "",
    action: "approve",
  });

  const [rejectDialog, setRejectDialog] = useState<{
    isOpen: boolean;
    requestId: string;
  }>({
    isOpen: false,
    requestId: "",
  });

  const {
    data: moderationOrdersResponse,
    isLoading,
    refetch,
  } = useGetOnModerationOrders({
    query: {
      refetchInterval: 1 * 60 * 1000,
    },
  });

  const {
    data: continueRebiddingResponse,
    isLoading: isContinueRebiddingLoading,
    refetch: refetchContinueRebidding,
  } = useGetContinueRebiddingRequests({
    query: {
      refetchInterval: 1 * 60 * 1000,
    },
  });

  const {
    data: cancelOrdersResponse,
    isLoading: isCancelOrdersLoading,
    refetch: refetchCancelOrders,
  } = useGetCancelOrders({
    query: {
      refetchInterval: 1 * 60 * 1000,
    },
  });

  const requests: OnModerationOrderDto[] = useMemo(
    () => moderationOrdersResponse?.data?.payload || [],
    [moderationOrdersResponse?.data?.payload],
  );
  const retradeExtensions = useMemo(
    () => continueRebiddingResponse?.data?.payload || [],
    [continueRebiddingResponse?.data?.payload],
  );
  const cancelOrders = useMemo(
    () => cancelOrdersResponse?.data?.payload || [],
    [cancelOrdersResponse?.data?.payload],
  );

  const [extensionConfirmModal, setExtensionConfirmModal] = useState<{
    isOpen: boolean;
    requestId: string;
    orderNumber: string;
  }>({
    isOpen: false,
    requestId: "",
    orderNumber: "",
  });

  const [extensionCancelDialog, setExtensionCancelDialog] = useState<{
    isOpen: boolean;
    requestId: string;
    orderNumber: string;
  }>({
    isOpen: false,
    requestId: "",
    orderNumber: "",
  });

  const [cancellationRejectModal, setCancellationRejectModal] = useState<{
    isOpen: boolean;
    requestId: string;
  }>({
    isOpen: false,
    requestId: "",
  });

  const [cancellationConfirmModal, setCancellationConfirmModal] = useState<{
    isOpen: boolean;
    requestId: string;
  }>({
    isOpen: false,
    requestId: "",
  });

  const { mutateAsync: mutateAcceptOrderAsync, isPending: isAcceptOrderPending } = useAcceptOrder();
  const {
    mutateAsync: mutateApproveContinueRebiddingAsync,
    isPending: isApproveContinueRebiddingPending,
  } = useApproveContinueRebidding();
  const { mutateAsync: mutateAcceptOrder1Async, isPending: isAcceptOrder1Pending } =
    useAcceptOrder1();

  const handleModerationAction = useCallback((requestId: string, action: "approve" | "reject") => {
    if (action === "reject") {
      // For reject action, open the comment dialog instead
      setRejectDialog({
        isOpen: true,
        requestId,
      });
    } else {
      // For approve action, use the existing confirm modal
      setConfirmModal({
        isOpen: true,
        requestId,
        action,
      });
    }
  }, []);

  const handleApprove = useCallback(
    (requestId: string) => handleModerationAction(requestId, "approve"),
    [handleModerationAction],
  );

  const handleReject = useCallback(
    (requestId: string) => handleModerationAction(requestId, "reject"),
    [handleModerationAction],
  );

  const handleConfirm = () => {
    const isAccept = confirmModal.action === "approve";
    mutateAcceptOrderAsync({
      data: {
        id: confirmModal.requestId,
        isAccept,
      },
    })
      .then(() => refetch())
      .then(() => {
        const actionText = isAccept ? "утверждена" : "отклонена";
        toast({
          title: `Заявка ${actionText}`,
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка модерации",
          description:
            error instanceof Error ? error.message : "Произошла ошибка при модерации заявки",
        });
      });
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleRejectWithComment = (requestId: string, comment: string) => {
    mutateAcceptOrderAsync({
      data: {
        id: requestId,
        isAccept: false,
        comment,
      },
    })
      .then(() => refetch())
      .then(() => {
        toast({
          title: "Заявка отклонена",
        });
        setRejectDialog({ isOpen: false, requestId: "" });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка модерации",
          description:
            error instanceof Error ? error.message : "Произошла ошибка при модерации заявки",
        });
      });
  };

  const handleViewDetails = useCallback(
    (requestId: string) => {
      setLocation(ROUTES.ORDER_ID.replace(":id", requestId));
    },
    [setLocation],
  );

  const handleExtensionApproveClick = useCallback(
    (requestId: string) => {
      const request = retradeExtensions.find((item) => item.id === requestId);

      setExtensionConfirmModal({
        isOpen: true,
        requestId,
        orderNumber: request?.orderNumber ?? "",
      });
    },
    [retradeExtensions],
  );

  const handleExtensionApproveConfirm = () => {
    mutateApproveContinueRebiddingAsync({
      data: {
        id: extensionConfirmModal.requestId,
        approve: true,
      },
    })
      .then(() => refetchContinueRebidding())
      .then(() => {
        toast({
          title: "Продление переторжки утверждено",
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка продления переторжки",
          description:
            error instanceof Error ? error.message : "Произошла ошибка при продлении переторжки",
        });
      })
      .finally(() => {
        setExtensionConfirmModal((prev) => ({ ...prev, isOpen: false }));
      });
  };

  const handleExtensionCancel = useCallback(
    (requestId: string) => {
      const request = retradeExtensions.find((item) => item.id === requestId);

      setExtensionCancelDialog({
        isOpen: true,
        requestId,
        orderNumber: request?.orderNumber ?? "",
      });
    },
    [retradeExtensions],
  );

  const handleExtensionCancelConfirm = (comment: string) => {
    mutateApproveContinueRebiddingAsync({
      data: {
        id: extensionCancelDialog.requestId,
        approve: false,
        comment,
      },
    })
      .then(() => refetchContinueRebidding())
      .then(() => {
        toast({
          title: "Продление переторжки отклонено",
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка отклонения продления переторжки",
          description:
            error instanceof Error ? error.message : "Произошла ошибка при отклонении продления",
        });
      })
      .finally(() => {
        setExtensionCancelDialog({
          isOpen: false,
          requestId: "",
          orderNumber: "",
        });
      });
  };

  const handleCancellationApproveClick = useCallback((requestId: string) => {
    setCancellationConfirmModal({
      isOpen: true,
      requestId,
    });
  }, []);

  const handleCancellationApproveConfirm = () => {
    mutateAcceptOrder1Async({
      data: {
        id: cancellationConfirmModal.requestId,
        isAccept: true,
      },
    })
      .then(() => refetchCancelOrders())
      .then(() => {
        toast({
          title: "Отмена заявки утверждена",
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка утверждения отмены заявки",
          description:
            error instanceof Error
              ? error.message
              : "Произошла ошибка при утверждении отмены заявки",
        });
      })
      .finally(() => {
        setCancellationConfirmModal((prev) => ({ ...prev, isOpen: false }));
      });
  };

  const handleCancellationCancelClick = useCallback((requestId: string) => {
    setCancellationRejectModal({
      isOpen: true,
      requestId,
    });
  }, []);

  const handleCancellationCancelConfirm = () => {
    mutateAcceptOrder1Async({
      data: {
        id: cancellationRejectModal.requestId,
        isAccept: false,
      },
    })
      .then(() => refetchCancelOrders())
      .then(() => {
        toast({
          title: "Отмена заявки отклонена",
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка отклонения отмены заявки",
          description:
            error instanceof Error
              ? error.message
              : "Произошла ошибка при отклонении отмены заявки",
        });
      })
      .finally(() => {
        setCancellationRejectModal({
          isOpen: false,
          requestId: "",
        });
      });
  };

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-text-heading">Модерация заявок</h2>
        <p className="caption mt-1">Проверка и утверждение заявок на перевозку груза</p>
      </div>

      <Tabs defaultValue="requests" className="w-full">
        <ModerationFilters
          requestsCount={requests.length}
          // profileRequestsCount={0}
          retradeExtensionCount={retradeExtensions.length}
          requestCancellationCount={cancelOrders.length}
        />

        <TabsContent value="requests">
          <Card>
            <CardHeader className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <CardTitle>Заявки на модерации</CardTitle>
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 w-fit self-end">
                  {requests.length} на модерации
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
              <ModerationRequestsTable
                requests={requests}
                isLoading={isLoading}
                onApprove={handleApprove}
                onReject={handleReject}
                onDelete={(_requestId: string) => {}} // TODO: add delete request mutation
                isPending={isAcceptOrderPending}
                onViewDetails={handleViewDetails}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="retrade-extensions">
          <Card>
            <CardHeader className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <CardTitle className="flex md:items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Запросы на продление переторжки
                </CardTitle>
                <Badge variant="secondary" className="bg-blue-100 text-blue-800 w-fit self-end">
                  {retradeExtensions.length} на продлении
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
              <ExtensionRebiddingTable
                requests={retradeExtensions}
                isLoading={isContinueRebiddingLoading}
                isPending={isApproveContinueRebiddingPending}
                onApprove={handleExtensionApproveClick}
                onCancel={handleExtensionCancel}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="request-cancellations">
          <Card>
            <CardHeader className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Запросы на отмену заявки
                </CardTitle>
                <Badge variant="secondary" className="bg-red-100 text-red-800 w-fit self-end">
                  {cancelOrders.length} на отмену заявки
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
              <CancellationTable
                requests={cancelOrders}
                isLoading={isCancelOrdersLoading}
                isPending={isAcceptOrder1Pending}
                onApprove={handleCancellationApproveClick}
                onCancel={handleCancellationCancelClick}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Утверждение заявки"
        message="Вы уверены, что хотите утвердить эту заявку?"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        confirmText={confirmModal.action === "approve" ? "Утвердить" : "Отклонить"}
      />

      <ConfirmModal
        isOpen={extensionConfirmModal.isOpen}
        title="Продление переторжки"
        message={`Вы уверены, что хотите продлить переторжку по заявке № ${extensionConfirmModal.orderNumber}?`}
        onConfirm={handleExtensionApproveConfirm}
        onCancel={() => setExtensionConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        confirmText="Подтвердить"
      />

      <CommentDialog
        isOpen={extensionCancelDialog.isOpen}
        title="Отклонение продления переторжки"
        description={`Укажите причину отклонения продления переторжки по заявке № ${extensionCancelDialog.orderNumber}`}
        isPending={isApproveContinueRebiddingPending}
        onConfirm={handleExtensionCancelConfirm}
        onCancel={() =>
          setExtensionCancelDialog({
            isOpen: false,
            requestId: "",
            orderNumber: "",
          })
        }
      />

      <CommentDialog
        isOpen={rejectDialog.isOpen}
        title="Отклонение заявки"
        description="Укажите причину отклонения заявки. Этот комментарий будет виден инициатору."
        isPending={isAcceptOrderPending}
        onConfirm={(comment) => {
          handleRejectWithComment(rejectDialog.requestId, comment);
        }}
        onCancel={() => setRejectDialog({ isOpen: false, requestId: "" })}
      />

      <ConfirmModal
        isOpen={cancellationConfirmModal.isOpen}
        title="Утверждение отмены заявки"
        message="Вы уверены, что хотите утвердить отмену этой заявки?"
        onConfirm={handleCancellationApproveConfirm}
        onCancel={() => setCancellationConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        confirmText="Утвердить"
      />

      <ConfirmModal
        isOpen={cancellationRejectModal.isOpen}
        title="Отклонение отмены заявки"
        message="Вы уверены, что хотите отклонить запрос на отмену заявки?"
        onConfirm={handleCancellationCancelConfirm}
        onCancel={() => setCancellationRejectModal((prev) => ({ ...prev, isOpen: false }))}
        confirmText="Отклонить"
      />

      <LoadingModal isOpen={isAcceptOrderPending} message="Обработка заявки..." />
    </>
  );
}
